import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { realGbp } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Lock } from "lucide-react";


const PERKS = ["Premium badge on your account", "Early access to new premium tools", "Priority support from the Finlo team", "Help keep Finlo independent and ad-free"];

export default function PlanPicker() {
  const [plan, setPlan] = useState("yearly");
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setPlans(data.plans)).catch(() => toast.error("Couldn't load plans"));
  }, []);
  const current = plans.find((p) => p.id === plan);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="mm-glow-card p-6 sm:p-8" data-testid="plan-picker">
      <div className="grid sm:grid-cols-2 gap-3">
        {plans.map((p) => (
          <button key={p.id} data-testid={`plan-option-${p.id}`} onClick={() => setPlan(p.id)}
            className={`relative text-left rounded-2xl border p-5 transition-[border-color,background-color] duration-200 ${plan === p.id ? "border-[#00C896] bg-[#00C896]/[0.07]" : "border-white/10 bg-[#0D0D0D] hover:border-white/25"}`}>
            {p.saving && <span className="absolute -top-2.5 right-4 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-[#00C896] text-[#04140D] px-2 py-0.5">Best value</span>}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-300">{p.label}</span>
              <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${plan === p.id ? "border-[#00C896] bg-[#00C896]" : "border-white/30"}`}>
                {plan === p.id && <Check className="h-3 w-3 text-[#04140D]" />}
              </span>
            </div>
            <p className="mt-3 font-num text-3xl font-extrabold">{realGbp(p.price)}<span className="text-sm font-sans font-normal text-slate-500"> / {p.interval}</span></p>
            <p className="text-xs text-[#4DE3BF] mt-1">{p.saving ? `Save ${realGbp(p.saving)} a year` : "Cancel anytime"}</p>
          </button>
        ))}
      </div>
      <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
        {PERKS.map((perk) => (
          <li key={perk} className="flex items-start gap-2 text-sm text-slate-300"><Check className="h-4 w-4 text-[#00C896] mt-0.5 shrink-0" /> {perk}</li>
        ))}
      </ul>
      <Button data-testid="plan-checkout-button" onClick={checkout} disabled={loading || !current} className="mt-7 w-full h-12 rounded-xl bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold text-base">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : `Continue to secure checkout${current ? ` · ${realGbp(current.price)}` : ""}`}
      </Button>
      <p className="mt-3 text-[11px] text-slate-500 flex items-center justify-center gap-1.5"><Lock className="h-3 w-3" /> Payments processed securely by Stripe. Prices include VAT. Renews automatically until cancelled.</p>
    </div>
  );
}
